import { Box, IconButton, Typography, Button, Stack, Grid2 as Grid, Checkbox, FormControlLabel } from '@mui/material';
import { useFieldArray, useWatch, Controller } from 'react-hook-form';
import { useEffect } from 'react';
import { RiDeleteBin6Line } from 'react-icons/ri';
import FormSchemaParser from '@/components/Common/FormSchemaParser';
import Select from '@/components/Common/Select';

interface AthenaHealthFormProps {
  namePrefix: string;
  control: any;
  getValues: any;
  disabled?: boolean;
  configFields: any;
  spacing: string;
  submitForm?: any;
}

const AthenaHealthForm = ({
  namePrefix,
  control,
  disabled,
  configFields,
  spacing,
  submitForm,
  getValues
}: AthenaHealthFormProps) => {
  const providersField = configFields?.find((f: any) => f.key === 'providers');
  const otherFields = configFields?.filter((f: any) => f.key !== 'providers');

  const {
    fields: providers,
    append,
    remove
  } = useFieldArray({
    control,
    name: `${namePrefix}.providers`
  });

  useWatch({
    control,
    name: `${namePrefix}.providers`
  });

  useEffect(() => {
    const existing = getValues(`${namePrefix}.providers`);

    if (!existing || existing.length === 0) {
      append({
        provider_name: '',
        accept_new_patient: false
      });
    }
  }, [append, getValues, namePrefix]);

  return (
    <>
      {otherFields?.length > 0 && (
        <FormSchemaParser
          disabled={disabled}
          fields={otherFields}
          control={control}
          namePrefix={namePrefix}
          gridSize={12}
          spacing={spacing}
          submitForm={(val: any) => submitForm?.(val)}
        />
      )}

      <Box>
        <Typography fontSize={16} fontWeight={600} my={2}>
          Provider Details
        </Typography>

        {providers.map((provider: any, index: number) => (
          <ProviderItem
            key={provider.id}
            index={index}
            control={control}
            remove={remove}
            disabled={disabled}
            namePrefix={namePrefix}
            providersFields={providersField}
            totalProviders={providers.length}
          />
        ))}

        <Box display='flex' justifyContent='center'>
          <Button
            variant='contained'
            disabled={disabled}
            onClick={() =>
              append({
                provider_name: '',
                accept_new_patient: false
              })
            }
          >
            + Add Another Provider
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default AthenaHealthForm;

const ProviderItem = ({ index, control, remove, disabled, namePrefix, totalProviders, providersFields }: any) => {
  const providerPrefix = `${namePrefix}.providers.${index}`;
  const providerField = providersFields?.group?.find((f: any) => f.key === 'provider_name');
  const providerOptions = providerField?.options || [];

  return (
    <Box p={3} mb={3} border='1px solid #E5E7EB' borderRadius='12px'>
      <Stack direction='row' justifyContent='space-between' mb={2}>
        <Typography fontWeight={600}>Provider #{index + 1}</Typography>

        {totalProviders > 1 && !disabled && (
          <IconButton color='error' onClick={() => remove(index)}>
            <RiDeleteBin6Line />
          </IconButton>
        )}
      </Stack>

      <Grid container spacing={5} alignItems='center'>
        <Grid size={9}>
          <Select
            label='Select Provider'
            name={`${providerPrefix}.provider_name`}
            control={control}
            disabled={disabled}
            options={providerOptions}
            fullWidth
          />
        </Grid>

        <Grid size={3}>
          <Controller
            name={`${providerPrefix}.accept_new_patient`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={disabled}
                  />
                }
                label='Accept New Patients'
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
